import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, Shield, CheckCircle2, AlertCircle, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUB_TYPES = [
  { value: 'monthly', label: 'Monthly memberships' },
  { value: 'annual', label: 'Annual plans' },
  { value: 'both', label: 'Monthly + Annual' },
  { value: 'mixed', label: 'Subscriptions + one-time products' },
  { value: 'one_time', label: 'One-time products only' },
];

const initialForm = {
  application_type: 'subscription_partner', // 'subscription_partner' | 'affiliate_partner'
  company_name: '', website: '', business_email: '', contact_name: '',
  password: '', description: '', subscription_type: 'monthly',
  social_1_url: '', social_2_url: '', supporting_notes: '',
  // Affiliate-partner fields
  affiliate_url: '', proposed_category: '', customer_benefit: '', proposed_payout: '',
  terms_accepted: false,
};

export default function PartnerApply() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();

  // Pre-select Affiliate tab when arriving from
  // /submit's "Apply as Affiliate Partner" link (?type=affiliate).
  // The submit-page links are the primary discovery surface — having
  // them land already-toggled removes one click from the warmest
  // applicant flow (founders who already self-identified as affiliate).
  useEffect(() => {
    const t = (searchParams.get('type') || '').toLowerCase();
    if (t === 'affiliate' || t === 'affiliate_partner') {
      setForm((f) => ({ ...f, application_type: 'affiliate_partner' }));
    }
  }, [searchParams]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.terms_accepted) {
      toast.error('Please accept the partner terms to continue.');
      return;
    }
    setSubmitting(true);
    try {
      // Only send fields the chosen application_type actually needs.
      // Sending `password: ""` would fail the backend Pydantic
      // min_length=8 check before our affiliate-path validator runs.
      const payload = { ...form };
      if (form.application_type === 'affiliate_partner') {
        delete payload.password;
        delete payload.subscription_type;
        // Keep social fields out — they're not validated for affiliates
        // and empty strings would still trip the URL regex.
        delete payload.social_1_url;
        delete payload.social_2_url;
      } else {
        delete payload.affiliate_url;
        delete payload.proposed_category;
        delete payload.customer_benefit;
        delete payload.proposed_payout;
      }
      const res = await axios.post(`${API_URL}/api/partners/apply`, payload);
      setResult(res.data);
      toast.success('Application submitted. Check your business email for updates.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="max-w-2xl w-full bg-neutral-900/60 border border-white/10 rounded-2xl p-8 md:p-12">
          <CheckCircle2 className="w-14 h-14 text-[#00F0FF] mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Application received
          </h1>
          <p className="text-neutral-400 mb-8">
            {result.message || (result.application_type === 'affiliate_partner'
              ? "We review every affiliate submission personally — you'll hear back at the email you provided within a few business days."
              : "We ran an automated verification on your company details. An admin will review your application and reply to your business email.")}
          </p>
          <div className="bg-black/40 border border-white/10 rounded-xl p-5 space-y-3 mb-6">
            <div className="flex justify-between">
              <span className="text-neutral-500 text-sm">Application ID</span>
              <span className="font-mono text-sm">{result.application_id}</span>
            </div>
            {result.application_type && (
              <div className="flex justify-between">
                <span className="text-neutral-500 text-sm">Type</span>
                <span className="capitalize">{result.application_type.replace('_', ' ')}</span>
              </div>
            )}
            {/* Verification score + auto recommendation only exist on the
                subscription-partner path (the affiliate path skips the
                heuristic scorecard). Render only when present. */}
            {result.verification_score != null && (
              <div className="flex justify-between">
                <span className="text-neutral-500 text-sm">Verification score</span>
                <span className="text-[#00F0FF] font-bold">{result.verification_score}/100</span>
              </div>
            )}
            {result.auto_recommendation && (
              <div className="flex justify-between">
                <span className="text-neutral-500 text-sm">Automated recommendation</span>
                <span className="capitalize">{result.auto_recommendation?.replace('_', ' ')}</span>
              </div>
            )}
          </div>
          <Link to="/">
            <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold">
              Back to marketplace <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-20">
        <div className="mb-10">
          <div className="flex items-center gap-2 text-[#00F0FF] text-sm uppercase font-bold mb-3">
            <Shield className="w-4 h-4" /> Partner Application
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            {form.application_type === 'affiliate_partner'
              ? 'Get your AI tool featured'
              : 'Sell subscriptions on theailedger.ai'}
          </h1>
          <p className="text-neutral-400 text-lg">
            {form.application_type === 'affiliate_partner'
              ? "Submit your AI product and we'll review you for a featured slot on our affiliate partners page. Used by tools like Traxy, Atoms, and HeyPocket — no setup, no Stripe Connect, no monthly fee. Just a referral link, your logo, and a clear customer benefit."
              : 'For established AI companies, SaaS brands and partner platforms who want to offer memberships and recurring plans to our buyer base. Applications are verified automatically against your website and social accounts, then reviewed by our admin team.'}
          </p>

          {/* Application type selector — gates the form below */}
          <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-neutral-900/40 border border-white/10 rounded-xl">
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, application_type: 'subscription_partner' }))}
              className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                form.application_type === 'subscription_partner'
                  ? 'bg-[#00F0FF] text-black'
                  : 'text-neutral-400 hover:text-white'
              }`}
              data-testid="partner-type-subscription"
            >
              Subscription Partner
              <span className="block text-[10px] font-normal opacity-75 mt-0.5">Sell memberships on our marketplace</span>
            </button>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, application_type: 'affiliate_partner' }))}
              className={`px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                form.application_type === 'affiliate_partner'
                  ? 'bg-[#00F0FF] text-black'
                  : 'text-neutral-400 hover:text-white'
              }`}
              data-testid="partner-type-affiliate"
            >
              Affiliate Partner
              <span className="block text-[10px] font-normal opacity-75 mt-0.5">Get featured · link out to your site</span>
            </button>
          </div>

          {/* Cross-sell to Platform Partner lane for smaller / pre-revenue
              startups who'd be a better fit there ($99/mo) than at the
              enterprise tier (custom pricing, full Stripe Connect onboarding) */}
          {form.application_type === 'subscription_partner' && (
            <div className="mt-5 flex items-start gap-3 rounded-lg border border-[#FFB400]/30 bg-[#FFB400]/5 p-4" data-testid="partner-apply-platform-redirect">
            <Sparkles className="w-5 h-5 text-[#FFB400] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-[#FFB400] mb-1">
                Pre-revenue or early-stage all-in-one platform?
              </p>
              <p className="text-neutral-300 leading-relaxed">
                If you don't sell individual subscriptions on our marketplace and
                just want a featured slot to drive signups to your own platform,
                you're better off in our <strong>Featured Platform Partner</strong> lane —{' '}
                <Link to="/partners/platforms" className="text-[#FFB400] underline font-semibold hover:text-[#FFB400]/80" data-testid="partner-apply-platform-redirect-link">
                  $99/mo lifetime founding rate, no affiliate program required →
                </Link>
              </p>
            </div>
          </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="partner-apply-form">
          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#00F0FF]" /> Company
            </h2>

            <Field label="Company name" required>
              <Input
                required
                value={form.company_name}
                onChange={update('company_name')}
                placeholder="Galaxy AI, Inc."
                className="bg-black/40 border-white/10"
                data-testid="partner-company-name"
              />
            </Field>

            <Field label="Website" required hint="Your primary business URL. We check that it resolves and references your company.">
              <Input
                required
                type="url"
                value={form.website}
                onChange={update('website')}
                placeholder="https://galaxy.ai"
                className="bg-black/40 border-white/10"
                data-testid="partner-website"
              />
            </Field>

            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Business email" required hint="Use an email on your company domain when possible.">
                <Input
                  required
                  type="email"
                  value={form.business_email}
                  onChange={update('business_email')}
                  placeholder="partnerships@galaxy.ai"
                  className="bg-black/40 border-white/10"
                  data-testid="partner-email"
                />
              </Field>
              <Field label="Contact name" required>
                <Input
                  required
                  value={form.contact_name}
                  onChange={update('contact_name')}
                  placeholder="Your full name"
                  className="bg-black/40 border-white/10"
                  data-testid="partner-contact-name"
                />
              </Field>
            </div>

            {form.application_type === 'subscription_partner' && (
              <Field label="Account password" required hint="You will use this to log in once approved.">
                <Input
                  required
                  type="password"
                  minLength={8}
                  value={form.password}
                  onChange={update('password')}
                  placeholder="Minimum 8 characters"
                  className="bg-black/40 border-white/10"
                  data-testid="partner-password"
                />
              </Field>
            )}
          </div>

          {form.application_type === 'affiliate_partner' && (
            <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
              <h2 className="text-lg font-bold">Affiliate program</h2>
              <Field label="Your affiliate / referral URL" required hint="The link we'll attach to your card on our affiliate partners page. Include any ?ref= or tracking params.">
                <Input
                  required
                  type="url"
                  value={form.affiliate_url}
                  onChange={update('affiliate_url')}
                  placeholder="https://yourtool.ai/?ref=theailedger"
                  className="bg-black/40 border-white/10"
                  data-testid="partner-affiliate-url"
                />
              </Field>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Category" hint="Where it fits — e.g. Sales & Pipeline, Writing, Image, Audio.">
                  <Input
                    value={form.proposed_category}
                    onChange={update('proposed_category')}
                    placeholder="Sales & Pipeline"
                    className="bg-black/40 border-white/10"
                    data-testid="partner-proposed-category"
                  />
                </Field>
                <Field label="Commission / payout terms (optional)" hint="What you're offering. Helps prioritize review.">
                  <Input
                    value={form.proposed_payout}
                    onChange={update('proposed_payout')}
                    placeholder="30% recurring · 90-day cookie"
                    className="bg-black/40 border-white/10"
                    data-testid="partner-proposed-payout"
                  />
                </Field>
              </div>
              <Field label="Customer benefit" hint="One sharp line that tells our buyers WHY they should click.">
                <Input
                  value={form.customer_benefit}
                  onChange={update('customer_benefit')}
                  maxLength={240}
                  placeholder="Turn LinkedIn engagement into qualified pipeline"
                  className="bg-black/40 border-white/10"
                  data-testid="partner-customer-benefit"
                />
              </Field>
            </div>
          )}

          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
            <h2 className="text-lg font-bold">
              {form.application_type === 'affiliate_partner' ? 'Tell us about your tool' : 'Product & plans'}
            </h2>

            <Field label={form.application_type === 'affiliate_partner' ? 'What does your tool do?' : 'What will you sell?'} required hint="Tell us about your product, target buyer, and what makes it a fit.">
              <Textarea
                required
                minLength={20}
                value={form.description}
                onChange={update('description')}
                placeholder={form.application_type === 'affiliate_partner'
                  ? 'Describe your product, who it serves, and why our buyers would care. 20+ characters.'
                  : 'Describe your subscription product, pricing tiers, target customer, and why theailedger.ai audience is a fit.'}
                className="bg-black/40 border-white/10 min-h-[130px]"
                data-testid="partner-description"
              />
            </Field>

            {form.application_type === 'subscription_partner' && (
              <Field label="Subscription type" required>
                <select
                  value={form.subscription_type}
                  onChange={update('subscription_type')}
                  className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-white"
                  data-testid="partner-subscription-type"
                >
                  {SUB_TYPES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
            )}
          </div>

          {form.application_type === 'subscription_partner' && (
          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
            <h2 className="text-lg font-bold">Proof of legitimacy</h2>
            <p className="text-sm text-neutral-400">
              Two active social or professional accounts for the company. We auto-check that
              they&apos;re reachable and reference your brand.
            </p>

            <Field label="Social account 1 (X, LinkedIn, Instagram, etc.)" required>
              <Input
                required
                type="url"
                value={form.social_1_url}
                onChange={update('social_1_url')}
                placeholder="https://twitter.com/galaxyai"
                className="bg-black/40 border-white/10"
                data-testid="partner-social-1"
              />
            </Field>
            <Field label="Social account 2" required>
              <Input
                required
                type="url"
                value={form.social_2_url}
                onChange={update('social_2_url')}
                placeholder="https://linkedin.com/company/galaxyai"
                className="bg-black/40 border-white/10"
                data-testid="partner-social-2"
              />
            </Field>
          </div>
          )}

          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-5 space-y-4">
            <Field label="Anything else (optional)" hint={form.application_type === 'affiliate_partner' ? 'Press mentions, audience size, related partner programs.' : 'Incorporation docs, press mentions, partner agreements, domain ownership proof.'}>
              <Textarea
                value={form.supporting_notes}
                onChange={update('supporting_notes')}
                placeholder="Optional — links or notes that will speed up review."
                className="bg-black/40 border-white/10 min-h-[80px]"
                data-testid="partner-supporting-notes"
              />
            </Field>
          </div>

          <label className="flex items-start gap-3 text-sm text-neutral-300">
            <input
              type="checkbox"
              checked={form.terms_accepted}
              onChange={(e) => setForm((f) => ({ ...f, terms_accepted: e.target.checked }))}
              className="mt-1 w-4 h-4 accent-[#00F0FF]"
              data-testid="partner-terms-checkbox"
              required
            />
            <span>
              I am authorised to represent this company and accept the&nbsp;
              <Link to="/terms" target="_blank" className="text-[#00F0FF] hover:underline">Partner Terms</Link>,&nbsp;
              <Link to="/privacy" target="_blank" className="text-[#00F0FF] hover:underline">Privacy Policy</Link>&nbsp;
              and refund policy.
            </span>
          </label>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Automated verification runs on submit and is visible to our admin team only.
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold px-6"
              data-testid="submit-partner-application"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit partner application
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm text-white">
        {label}{required && <span className="text-[#00F0FF]">&nbsp;*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
    </div>
  );
}
